import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import './BillingDashboard.css';
import MovementDetail from './MovementDetail';
import BillingRules from './BillingRules';
import AircraftTrack from './AircraftTrack';
import { FlagIcon, AirlineLogo, AircraftTypeDisplay } from './AircraftTableComponents';
import { getAccessToken } from '../utils/auth';
import { getBillingDashboard, getBillingAircraft, getMovementTrack } from '../utils/httpApi';
import { getCountryFromHex } from '../utils/icaoCountryMap';

/**
 * Billing page (spec §4.1) — Predictions-style split: airport map on the
 * left, aircraft + movement list on the right. Clicking a movement loads
 * its GreptimeDB track and draws it on the map with the event markers.
 * KPIs sit in a thin header strip alongside the period controls.
 */

const AIRPORT = { icao: 'EIDW', name: 'Dublin', center: [53.4213, -6.2701], zoom: 14 };
const PERIODS = ['DAILY', 'WEEKLY', 'MONTHLY', 'RANGE'];

function todayIso() { return new Date().toISOString().slice(0, 10); }

function apiOk(resp) {
  const s = resp && resp.header && resp.header.apiStatus;
  return s === 'OK' || s === 'SUCCESS';
}

function money(amount, currency) {
  const n = Number(amount) || 0;
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency', currency: currency || 'EUR'
    }).format(n);
  } catch {
    return (currency || 'EUR') + ' ' + n.toFixed(2);
  }
}

/** Fits the Leaflet map to a track's bounds once its points have loaded. */
function FitTrack({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const lats = points.map((p) => p.latitude);
    const lons = points.map((p) => p.longitude);
    map.fitBounds(
      [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]],
      { padding: [60, 60], maxZoom: 15 }
    );
    // Leaflet sometimes leaves the map at its initial center/zoom when the
    // pane was sized 0 at first paint (race with the parent's flex layout).
    // Forcing an invalidate after the fit makes the polyline + markers fall
    // back within the visible bounds.
    setTimeout(() => { try { map.invalidateSize(); } catch {} }, 50);
  }, [points, map]);
  return null;
}

function BillingDashboard() {
  const [periodType, setPeriodType] = useState('DAILY');
  const [date, setDate] = useState(todayIso());
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [airline, setAirline] = useState('');

  const [data, setData] = useState(null);
  const [airlineOptions, setAirlineOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [detailMovementId, setDetailMovementId] = useState(null);
  const [aircraftData, setAircraftData] = useState(null);
  const [expandedAircraft, setExpandedAircraft] = useState(() => new Set());
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  const [track, setTrack] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);
  const [view, setView] = useState('movements'); // 'movements' | 'rules'

  const load = useCallback(() => {
    const token = getAccessToken();
    if (!token) { setStatus('Not authenticated.'); return; }
    const payload = { airportIcao: AIRPORT.icao, periodType };
    if (periodType === 'RANGE') { payload.fromDate = fromDate; payload.toDate = toDate; }
    else { payload.date = date; }
    if (airline) payload.airline = airline;

    setLoading(true);
    setStatus(null);
    getBillingDashboard(payload, token)
      .then((resp) => {
        if (!apiOk(resp)) {
          const errs = resp && resp.header && resp.header.errors;
          setStatus('Load failed: ' + (errs && errs[0] ? errs[0].description : 'error'));
          setData(null);
          return;
        }
        if (!resp || !resp.totals) {
          setStatus('No dashboard data returned.');
          setData(null);
          return;
        }
        setData(resp);
        if (!airline) {
          setAirlineOptions((resp.byAirline || []).map((r) => r.airline));
        }
      })
      .catch((e) => setStatus('Load error: ' + e.message))
      .finally(() => setLoading(false));
  }, [periodType, date, fromDate, toDate, airline]);

  useEffect(() => { load(); }, [load]);

  const loadAircraft = useCallback(() => {
    const token = getAccessToken();
    if (!token) return;
    const payload = { airportIcao: AIRPORT.icao, periodType };
    if (periodType === 'RANGE') { payload.fromDate = fromDate; payload.toDate = toDate; }
    else { payload.date = date; }
    if (airline) payload.airline = airline;
    getBillingAircraft(payload, token)
      .then((resp) => setAircraftData(apiOk(resp) && resp.aircraft ? resp : null))
      .catch(() => setAircraftData(null));
  }, [periodType, date, fromDate, toDate, airline]);

  useEffect(() => { loadAircraft(); }, [loadAircraft]);

  function toggleAircraft(icao) {
    setExpandedAircraft((prev) => {
      const next = new Set(prev);
      if (next.has(icao)) next.delete(icao); else next.add(icao);
      return next;
    });
  }

  /** Click a movement → load its track and draw it on the airport map. */
  const loadTrack = useCallback((movementId) => {
    setSelectedMovementId(movementId);
    const token = getAccessToken();
    if (!token) return;
    setTrackLoading(true);
    setTrack(null);
    getMovementTrack(AIRPORT.icao, movementId, token)
      .then((resp) => {
        setTrack(apiOk(resp)
          ? { points: resp.points || [], events: resp.events || [] }
          : { points: [], events: [] });
      })
      .catch(() => setTrack({ points: [], events: [] }))
      .finally(() => setTrackLoading(false));
  }, []);

  const currency = data && data.totals ? data.totals.currency : 'EUR';

  return (
    <div className="bdash bdash-split-page">
      <div className="bdash-topbar">
        <div className="bdash-topbar-left">
          <h2 className="bdash-title">Billing — {AIRPORT.name} ({AIRPORT.icao})</h2>
          <div className="bdash-tabs">
            <button
              className={'bdash-tab' + (view === 'movements' ? ' active' : '')}
              onClick={() => setView('movements')}>Movements</button>
            <button
              className={'bdash-tab' + (view === 'rules' ? ' active' : '')}
              onClick={() => setView('rules')}>Rules</button>
          </div>
          {view === 'movements' && (
            <div className="bdash-controls">
              <div className="bdash-periods">
                {PERIODS.map((p) => (
                  <button key={p}
                    className={'bdash-period' + (periodType === p ? ' active' : '')}
                    onClick={() => setPeriodType(p)}>{p}</button>
                ))}
              </div>
              {periodType !== 'RANGE' && (
                <input type="date" value={date}
                  onChange={(e) => setDate(e.target.value)} />
              )}
              {periodType === 'RANGE' && (
                <span className="bdash-dates">
                  <input type="date" value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)} />
                  <span>→</span>
                  <input type="date" value={toDate}
                    onChange={(e) => setToDate(e.target.value)} />
                </span>
              )}
              <select value={airline} onChange={(e) => setAirline(e.target.value)}>
                <option value="">All airlines</option>
                {airlineOptions.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          )}
        </div>
        {view === 'movements' && data && data.totals && (
          <div className="bdash-kpis-strip">
            <div className="bdash-kpi-mini">
              <span className="bdash-kpi-mini-value">{data.totals.aircraftSeen}</span>
              <span className="bdash-kpi-mini-label">aircraft</span>
            </div>
            <div className="bdash-kpi-mini">
              <span className="bdash-kpi-mini-value">{data.totals.movements}</span>
              <span className="bdash-kpi-mini-label">movements</span>
            </div>
            <div className="bdash-kpi-mini">
              <span className="bdash-kpi-mini-value">
                {money(data.totals.totalAmount, currency)}
              </span>
              <span className="bdash-kpi-mini-label">charges</span>
            </div>
          </div>
        )}
      </div>

      {view === 'rules' ? (
        <BillingRules airportIcao={AIRPORT.icao} />
      ) : (<>
      {status && <div className="bdash-status">{status}</div>}

      <div className="bdash-split">
        <div className="bdash-map-pane">
          <MapContainer
            center={AIRPORT.center}
            zoom={AIRPORT.zoom}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            {track && track.points.length > 0 && (
              <>
                <FitTrack points={track.points} />
                <AircraftTrack trackData={{ points: track.points }} />
                {/* S→A / A→S transitions: the same classification the
                    tracker uses to decide TAKEOFF / TOUCHDOWN. Rendered as
                    small dots on the track so a fake "takeoff at gate" is
                    visually obvious — the A dot will sit on the apron. */}
                {track.points.map((p, i) => {
                  if (i === 0) return null;
                  const prev = track.points[i - 1].posType;
                  const curr = p.posType;
                  if (!prev || !curr || prev === curr) return null;
                  const sToA = prev === 'S' && curr === 'A';
                  return (
                    <CircleMarker key={'tx' + i}
                      center={[p.latitude, p.longitude]} radius={4}
                      pathOptions={{
                        color: '#000', weight: 1,
                        fillColor: sToA ? '#ff7a18' : '#1e88e5',
                        fillOpacity: 1,
                      }}>
                      <Tooltip direction="top" offset={[0, -4]}>
                        {(sToA ? 'S → A' : 'A → S') + ' @ ' + (p.timestamp || '')}
                      </Tooltip>
                    </CircleMarker>
                  );
                })}
                {track.events.map((ev, i) => (
                  <CircleMarker key={i}
                    center={[ev.latitude, ev.longitude]} radius={7}
                    pathOptions={{ color: '#ffffff', weight: 2,
                      fillColor: '#ff7a18', fillOpacity: 1 }}>
                    <Tooltip permanent direction="top" offset={[0, -6]}>{ev.label}</Tooltip>
                  </CircleMarker>
                ))}
              </>
            )}
          </MapContainer>
          {trackLoading && <div className="bdash-map-overlay">Loading track…</div>}
          {!track && !trackLoading && (
            <div className="bdash-map-hint">
              Select a movement on the right to see its track.
            </div>
          )}
          {track && track.points.length === 0 && !trackLoading && (
            <div className="bdash-map-hint">No track recorded for this movement.</div>
          )}
        </div>

        <div className="bdash-list-pane">
          {loading && <div className="bdash-status">Loading…</div>}
          <div className="bdash-list-label">
            Aircraft ({aircraftData ? aircraftData.aircraft.length : 0})
            <span className="bdash-list-hint"> — click a movement for its track</span>
          </div>
          <div className="bdash-aircraft-list">
            {(aircraftData ? aircraftData.aircraft : []).map((ac) => {
              const open = expandedAircraft.has(ac.icaoAddress);
              return (
                <React.Fragment key={ac.icaoAddress}>
                  <div className="bdash-ac-row"
                    onClick={() => toggleAircraft(ac.icaoAddress)}>
                    <span className="bdash-caret">{open ? '▾' : '▸'}</span>
                    <FlagIcon countryCode={getCountryFromHex(ac.icaoAddress)} />
                    <span className="bdash-hex">{ac.icaoAddress}</span>
                    <span className="bdash-reg">{ac.registration || '—'}</span>
                    <span className="bdash-type">
                      <AircraftTypeDisplay aircraftType={ac.aircraftType} />
                    </span>
                    <span className="bdash-airline">
                      <AirlineLogo airlineCode={ac.airlineCode} airlineName={ac.airlineName} />
                      <span>{ac.airlineName || 'Unknown'}</span>
                    </span>
                    <span className="bdash-charge">{money(ac.totalCharges, currency)}</span>
                  </div>
                  {open && ac.movements.map((m) => (
                    <div key={m.movementId}
                      className={'bdash-mv-row' +
                        (selectedMovementId === m.movementId ? ' selected' : '')}
                      onClick={() => loadTrack(m.movementId)}
                      title="Show this movement's track on the map">
                      <span className="bdash-mv-time">{m.eventTime}</span>
                      <span className="bdash-mv-type">{m.movementType}</span>
                      <span className="bdash-mv-flight">
                        {m.flightIata || m.flightCallsign || '—'}
                      </span>
                      <span className="bdash-mv-route">
                        {(m.originIata || m.destinationIata)
                          ? (m.originIata || '???') + ' → ' + (m.destinationIata || '???')
                          : ''}
                      </span>
                      <span className="bdash-mv-loc">
                        {[m.runway && ('rwy ' + m.runway),
                          m.standId && ('stand ' + m.standId)]
                          .filter(Boolean).join(' · ') || '—'}
                      </span>
                      <span className="bdash-mv-charge">{money(m.totalCharges, currency)}</span>
                      <button className="bdash-mv-detail"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailMovementId(m.movementId);
                        }}
                        title="Open full detail">⋯</button>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
            {aircraftData && aircraftData.aircraft.length === 0 && (
              <div className="bdash-empty">No aircraft in this period</div>
            )}
            {!aircraftData && <div className="bdash-empty">Loading aircraft…</div>}
          </div>
        </div>
      </div>
      </>)}

      {detailMovementId != null && (
        <MovementDetail
          airportIcao={AIRPORT.icao}
          movementId={detailMovementId}
          onClose={() => setDetailMovementId(null)}
        />
      )}
    </div>
  );
}

export default BillingDashboard;
